import { useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import { TimeSlot, CreateReservationPayload } from './api';
import { PublicService } from './types';
import {
  usePublicProfileQuery,
  usePublicAvailableSlotsQuery,
  usePublicHighlightedDaysQuery,
  useCreatePublicReservationMutation,
} from './queries';
import { useCustomerAuth } from '@features/customer-reservations/CustomerAuthContext';
import {
  useReservationServicesQuery,
  useCreateCustomerVisitMutation,
  useCustomerAvailableSlotsQuery,
} from '@features/customer-reservations/queries';
import { queryClient } from '@shared/queries';

export type ContactForm = {
  firstName: string;
  lastName: string;
  phone: string;
  phonePrefix: string;
  email: string;
  note: string;
};

export const usePublicBookingPage = () => {
  const { userId: profileUserId } = useParams<{ userId: string }>();
  const { isAuthenticated, userId: authUserId, loading: authLoading } = useCustomerAuth();

  const [searchParams] = useSearchParams();
  const [selectedServiceId, setSelectedServiceId] = useState<string | null>(() =>
    searchParams.get('serviceId'),
  );
  const [step, setStep] = useState(() => (selectedServiceId ? 1 : 0));
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  const [currentMonth, setCurrentMonth] = useState(dayjs().add(48, 'hour'));
  const [contact, setContact] = useState<ContactForm>({
    firstName: '',
    lastName: '',
    phone: '',
    phonePrefix: '+420',
    email: '',
    note: '',
  });
  const [photos, setPhotos] = useState<string[]>([]);
  const [submitted, setSubmitted] = useState(false);

  // Always fetch the public profile (needed for guest services + profile header)
  const {
    data: profile,
    isLoading: loadingProfile,
    isError: profileError,
  } = usePublicProfileQuery(profileUserId);

  // Resolve public URL codes before comparing with the UUID in the session.
  const isAuthForProfile = isAuthenticated && !!profile?.userId && authUserId === profile.userId;

  // Authenticated: fetch allowed services from customer API
  const { data: servicesData, isLoading: isLoadingCustomerServices } =
    useReservationServicesQuery(isAuthForProfile);

  const services: PublicService[] = isAuthForProfile
    ? (servicesData?.services ?? []).map(s => ({
        id: s.id,
        serviceName: s.serviceName,
        publicName: s.publicName ?? '',
        publicDescription: s.publicDescription ?? '',
        basePrice: String(s.basePrice ?? ''),
        blocks: s.blocks ?? [],
      }))
    : (profile?.services ?? []);

  // Resolve the requested ID only against services available to this customer.
  const selectedService = services.find(service => service.id === selectedServiceId) ?? null;

  // Guest: public slots API; Authenticated: customer slots API (already has 48h filter)
  const selectedDateStr = selectedDate?.format('YYYY-MM-DD');

  const { data: publicSlotsData, isLoading: loadingPublicSlots } = usePublicAvailableSlotsQuery(
    !isAuthForProfile ? profileUserId : undefined,
    !isAuthForProfile ? selectedDateStr : undefined,
    !isAuthForProfile ? selectedService?.id : undefined,
  );

  const { data: customerSlots, isLoading: loadingCustomerSlots } = useCustomerAvailableSlotsQuery(
    isAuthForProfile ? selectedDateStr : undefined,
    isAuthForProfile ? selectedService?.id : undefined,
    isAuthForProfile,
  );

  // Highlighted days (public API works for both flows – shows which days have availability)
  const { data: highlightedDaysData, isFetching: loadingHighlightedDays } =
    usePublicHighlightedDaysQuery(
      profileUserId,
      currentMonth.format('YYYY-MM'),
      selectedService?.id,
    );

  // Mutations
  const publicMutation = useCreatePublicReservationMutation(profileUserId);
  const customerMutation = useCreateCustomerVisitMutation();

  // Derive merged values

  const slots: TimeSlot[] = isAuthForProfile
    ? (customerSlots ?? [])
    : (publicSlotsData?.slots ?? []);

  const slotsReason = isAuthForProfile ? undefined : publicSlotsData?.reason;

  const loadingSlots = isAuthForProfile ? loadingCustomerSlots : loadingPublicSlots;

  const handleSelectService = (service: PublicService) => {
    setSelectedServiceId(service.id);
    setSelectedDate(null);
    setSelectedSlot(null);
    setStep(1);
  };

  const handleSelectSlot = (slot: TimeSlot) => setSelectedSlot(slot);

  const handleDateChange = (date: Dayjs | null) => {
    setSelectedDate(date);
    setSelectedSlot(null);
  };

  const handleMonthChange = (month: Dayjs) => {
    setCurrentMonth(month);
    setSelectedDate(null);
    setSelectedSlot(null);
  };

  const MAX_PHOTOS = 2;

  const handleAddPhoto = (dataUrl: string) =>
    setPhotos(prev => (prev.length >= MAX_PHOTOS ? prev : [...prev, dataUrl]));

  const handleRemovePhoto = (index: number) =>
    setPhotos(prev => prev.filter((_, i) => i !== index));

  const handleSubmit = () => {
    if (!selectedService || !selectedDate || !selectedSlot) {return;}

    const startHour = parseInt(selectedSlot.start.split(':')[0], 10);
    const startMin = parseInt(selectedSlot.start.split(':')[1], 10);
    const endHour = parseInt(selectedSlot.end.split(':')[0], 10);
    const endMin = parseInt(selectedSlot.end.split(':')[1], 10);

    if (isAuthForProfile) {
      const date = selectedDate.hour(startHour).minute(startMin).second(0).millisecond(0).toDate();
      const dateTo = selectedDate.hour(endHour).minute(endMin).second(0).millisecond(0).toDate();

      customerMutation.mutate(
        { date, dateTo, serviceIds: selectedService.id, depositRequired: false },
        {
          onSuccess: () => {
            setSubmitted(true);
            queryClient.invalidateQueries({ queryKey: ['myVisits'] });
          },
        },
      );
    } else {
      const payload: CreateReservationPayload = {
        serviceId: selectedService.id,
        requestedDate: selectedDate
          .hour(startHour)
          .minute(startMin)
          .second(0)
          .millisecond(0)
          .toISOString(),
        requestedDateTo: selectedDate
          .hour(endHour)
          .minute(endMin)
          .second(0)
          .millisecond(0)
          .toISOString(),
        customerFirstName: contact.firstName.trim(),
        customerLastName: contact.lastName.trim(),
        customerPhone: contact.phone,
        customerPhonePrefix: contact.phonePrefix,
        customerEmail: contact.email.trim() || undefined,
        note: contact.note || undefined,
        customerPhotos: photos.length > 0 ? photos : undefined,
      };

      publicMutation.mutate(payload, { onSuccess: () => setSubmitted(true) });
    }
  };

  const isContactValid =
    contact.firstName.trim().length > 0 &&
    contact.lastName.trim().length > 0 &&
    contact.phone.trim().length > 0 &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email.trim());

  return {
    userId: profileUserId,
    isAuthForProfile,
    authLoading,
    step: selectedService ? step : 0,
    setStep,
    profile: profile ?? null,
    loadingProfile: loadingProfile || (isAuthForProfile && isLoadingCustomerServices),
    profileError: profileError ? 'Profil se nepodařilo načíst.' : null,
    selectedService,
    selectedDate,
    selectedSlot,
    slots,
    slotsReason,
    loadingSlots,
    highlightedDays: highlightedDaysData?.days ?? {},
    loadingHighlightedDays,
    currentMonth,
    contact,
    setContact,
    photos,
    maxPhotos: MAX_PHOTOS,
    handleAddPhoto,
    handleRemovePhoto,
    submitting: isAuthForProfile ? customerMutation.isPending : publicMutation.isPending,
    submitError: (isAuthForProfile ? customerMutation.isError : publicMutation.isError)
      ? (isAuthForProfile ? customerMutation.error?.message : publicMutation.error?.message) ||
        'Rezervaci se nepodařilo odeslat. Zkuste to prosím znovu.'
      : null,
    submitted,
    services,
    handleSelectService,
    handleSelectSlot,
    handleDateChange,
    handleMonthChange,
    canProceedToContact: !!selectedDate && !!selectedSlot,
    handleSubmit,
    isContactValid,
  };
};
